/* eslint-disable @typescript-eslint/no-inferrable-types */

import { ICloneable } from '../../contracts/cloneable.js';
import { IDataComparable } from '../../contracts/data-comparable.js';
import { SerializationException } from '../../exceptions/serialization-exception.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { isNotNullOrUndefined, isNullOrUndefined } from '../../utility/null-safety.js';
import { BusinessModuleType } from './business-module-type.js';

export class BusinessModuleRef implements ICloneable, IDataComparable {
  private static readonly BUSINESS_REF_DELIMITER: string = ':';

  public readonly encoded: string;

  private constructor(
    public readonly uniqueId: string,
    public readonly organizationId: string | null,
    public readonly moduleType: BusinessModuleType,
  ) {
    this.encoded = BusinessModuleRef.encode(uniqueId, organizationId, moduleType);

    if(uniqueId.length <= 0) {
      throw new Error('uniqueId must not be empty');
    }
  }

  static create(
      uniqueId: string,
      organizationId: string,
      moduleType: BusinessModuleType,
  ): BusinessModuleRef {
    return new BusinessModuleRef(
        uniqueId,
        organizationId,
        moduleType,
    );
  }

  static legacy(uniqueId: string): BusinessModuleRef {
    return new BusinessModuleRef(
        uniqueId,
        null,
        BusinessModuleType.Legacy,
    );
  }

  get isLegacy(): boolean {
    return this.moduleType === BusinessModuleType.Legacy;
  }

  encode(): string {
    return this.encoded;
  }

  toString(): string {
    return this.encoded;
  }

  clone(): this {
    if(isNullOrUndefined(this.organizationId) || this.organizationId.length <= 0) {
      return BusinessModuleRef.legacy(this.uniqueId) as this;
    }

    return BusinessModuleRef.create(this.uniqueId, this.organizationId, this.moduleType) as this;
  }

  isSame(other: this): boolean {
    if(this === other) {
      return true;
    }

    return other.encoded === this.encoded;
  }

  private static encode(
      uniqueId: string,
      organizationId: string | null = null,
      moduleType: BusinessModuleType | null = null,
  ): string {
    if(isNullOrUndefined(organizationId) || organizationId.length <= 0) {
      return uniqueId;
    }

    const result: string[] = [];

    result.push(organizationId);
    result.push(uniqueId);

    //  Disabled for backwards compatibility because In Time version 3.8 can't deserialize it!!!
    const actualModuleType: BusinessModuleType = moduleType ?? BusinessModuleType.Legacy;
    if(actualModuleType !== BusinessModuleType.Legacy) {
      result.push(fromEnumAsString(moduleType));
    }

    return result.join(this.BUSINESS_REF_DELIMITER);
  }

  static mustDecode(encodedRef: string): BusinessModuleRef {
    const optional = this.decode(encodedRef);
    if(isNullOrUndefined(optional)) {
      throw new SerializationException(`The provided string is not a valid encoded business module reference: ${encodedRef}`);
    }

    return optional;
  }

  static decode(encodedRef: string | null): BusinessModuleRef | null {
    if(isNotNullOrUndefined(encodedRef) && encodedRef.length > 0) {
      const components = encodedRef.split(this.BUSINESS_REF_DELIMITER);

      if(components.length >= 3) {
        return BusinessModuleRef.create(
            components[1],
            components[0],
            toEnumFromString(components[2], BusinessModuleType),
        );
      }
      else if(components.length == 2) {
        return BusinessModuleRef.create(
            components[1],
            components[0],
            BusinessModuleType.Legacy,
        );
      }
      else if(components.length == 1) {
        return BusinessModuleRef.legacy(components[0]);
      }
    }

    return null;
  }
}
